import { ReactNode } from 'react';
import { LucideIcon } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

/**
 * Single sanctioned accent treatment for hero titles.
 * Wrap the emphasized part of a hero title in <HeroAccent>.
 */
export const HeroAccent = ({ children, className }: { children: ReactNode; className?: string }) => (
  <span className={cn('bg-gradient-to-r from-amber-300 to-yellow-400 bg-clip-text text-transparent drop-shadow-sm', className)}>
    {children}
  </span>
);

/** Standard stat tile for hero stat rows (e.g. In-Demand Careers). */
export const HeroStatTile = ({
  icon: Icon,
  value,
  label,
}: {
  icon?: LucideIcon;
  value: ReactNode;
  label: ReactNode;
}) => (
  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
    {Icon && <Icon className="h-6 w-6 mx-auto mb-1" />}
    <div className="text-2xl font-bold">{value}</div>
    <div className="text-xs text-white/70">{label}</div>
  </div>
);

interface PageHeroProps {
  /** Optional pill above the title. One standard style across the site. */
  badge?: { icon?: LucideIcon; label: ReactNode };
  title: ReactNode;
  /** 'default' = text-4xl md:text-5xl, 'large' = text-4xl md:text-6xl (homepage-scale) */
  titleSize?: 'default' | 'large';
  subtitle?: ReactNode;
  /** Tailwind max-width class for the inner container. */
  maxWidth?: 'max-w-2xl' | 'max-w-3xl' | 'max-w-4xl';
  className?: string;
  /** CTAs, stat tiles, embedded tools — rendered below the subtitle. */
  children?: ReactNode;
}

/**
 * Unified page hero: one brand gradient everywhere.
 * Topics are differentiated via badge icon/label and <HeroAccent> title spans,
 * not background color.
 */
const PageHero = ({
  badge,
  title,
  titleSize = 'default',
  subtitle,
  maxWidth = 'max-w-4xl',
  className,
  children,
}: PageHeroProps) => {
  const BadgeIcon = badge?.icon;

  return (
    <section
      className={cn(
        'relative py-16 px-4 text-center bg-gradient-to-br from-wage-primary to-wage-primary-light text-white',
        className
      )}
    >
      <div className={cn('container mx-auto', maxWidth)}>
        {badge && (
          <Badge variant="outline" className="mb-6 border-white/20 bg-white/10 text-white">
            {BadgeIcon && <BadgeIcon className="w-4 h-4 mr-2" />}
            {badge.label}
          </Badge>
        )}
        <h1
          className={cn(
            'font-bold mb-5 animate-fade-in',
            titleSize === 'large' ? 'text-4xl md:text-6xl' : 'text-4xl md:text-5xl'
          )}
        >
          {title}
        </h1>
        {subtitle && (
          <p
            className="text-lg md:text-xl mb-8 text-white/80 max-w-2xl mx-auto animate-fade-in"
            style={{ animationDelay: '0.1s' }}
          >
            {subtitle}
          </p>
        )}
        {children}
      </div>
    </section>
  );
};

export default PageHero;
